import { useState, useEffect } from 'react'
import { RefreshCw, Eye, Play, FileArchive, CheckCircle, Loader2 } from 'lucide-react'
import Modal, { FormActions, Button } from '../shared/Modal'
import { getAPIBaseUrl } from '../../environment'
import './ReRunModal.css'

function authHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function formatTs(iso) {
  if (!iso) return null
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function ReRunModal({ isOpen, onClose, run, project, onReviewInWorkflow, onStartReRun }) {
  const [ingestData, setIngestData]     = useState(null)
  const [ingestLoading, setIngestLoading] = useState(false)

  // Fetch ingest job details when modal opens to show source artifact info
  useEffect(() => {
    if (!isOpen || !run) { setIngestData(null); return }

    const ingestJobId = run.job_ids?.['pipeline-upload']
    if (!ingestJobId) { setIngestData(null); return }

    setIngestLoading(true)
    fetch(`${getAPIBaseUrl()}/jobs/${ingestJobId}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => setIngestData(data?.output_data ?? null))
      .catch(() => setIngestData(null))
      .finally(() => setIngestLoading(false))
  }, [isOpen, run])

  if (!run) return null

  const displayName  = run.display_name || run.workflow_name || run.workflow_run_id.slice(-12)
  const nodeCount    = Object.keys(run.job_ids || {}).length
  const sourceHash   = ingestData?.summary?.source_hash
  const filesCount   = ingestData?.summary?.files_extracted

  const handleReview = () => { onReviewInWorkflow(run); onClose() }
  const handleStart  = () => {
    const ingestJobId = run.job_ids?.['pipeline-upload'] || null
    onStartReRun(run, {
      sourceHash:  sourceHash  || null,
      ingestJobId: ingestJobId || null,
      filesCount:  filesCount  ?? null,
    })
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Re-Run Transformation" size="medium">

      {/* Subtitle notice */}
      <p className="rerun-subtitle">
        A <strong>new run</strong> will be created. The original run and all its results remain unchanged.
      </p>

      {/* Previous run card */}
      <div className="rerun-run-card">
        <div className="rerun-run-card-left">
          <RefreshCw size={16} className="rerun-run-icon" />
          <div>
            <div className="rerun-run-name">{displayName}</div>
            <div className="rerun-run-id">{run.workflow_run_id}</div>
          </div>
        </div>
        <span className={`rerun-status-badge rerun-status-${(run.status || 'unknown').toLowerCase()}`}>
          {run.status || 'unknown'}
        </span>
      </div>

      {/* Metadata grid */}
      <div className="rerun-meta-grid">
        <div className="rerun-meta-row">
          <span className="rerun-meta-label">Project</span>
          <span className="rerun-meta-value">{run.workflow_name || project?.name || '—'}</span>
        </div>
        {run.created_at && (
          <div className="rerun-meta-row">
            <span className="rerun-meta-label">Originally created</span>
            <span className="rerun-meta-value">{formatTs(run.created_at)}</span>
          </div>
        )}
        {run.completed_at && (
          <div className="rerun-meta-row">
            <span className="rerun-meta-label">Completed</span>
            <span className="rerun-meta-value">{formatTs(run.completed_at)}</span>
          </div>
        )}
        {nodeCount > 0 && (
          <div className="rerun-meta-row">
            <span className="rerun-meta-label">Nodes completed</span>
            <span className="rerun-meta-value">{nodeCount} pipeline node{nodeCount !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* Source artifact box */}
      <div className="rerun-source-box">
        <FileArchive size={18} className="rerun-source-icon" />
        <div className="rerun-source-body">
          <div className="rerun-source-title">Source archive will be reused</div>
          {ingestLoading ? (
            <div className="rerun-source-detail">
              <Loader2 size={12} className="spin" /> Loading source info…
            </div>
          ) : sourceHash ? (
            <div className="rerun-source-detail">
              Hash: <code>{sourceHash.slice(0, 16)}…</code>
              {filesCount != null && <span className="rerun-source-dot">·</span>}
              {filesCount != null && <span>{filesCount} files extracted</span>}
            </div>
          ) : (
            <div className="rerun-source-detail">
              Original uploaded archive from project <strong>{run.workflow_name || project?.name}</strong>
            </div>
          )}
        </div>
        <CheckCircle size={16} className="rerun-source-check" />
      </div>

      {/* Action explanation */}
      <div className="rerun-action-info">
        <div className="rerun-action-info-row">
          <Eye size={14} />
          <div>
            <strong>Review in Workflow</strong> — Opens the workflow designer with this run's
            previous results loaded. Review or adjust nodes before executing again.
          </div>
        </div>
        <div className="rerun-action-info-row">
          <Play size={14} />
          <div>
            <strong>Start Re-Run</strong> — Opens the workflow designer ready to run.
            All previous context is loaded; click Run to begin a fresh execution.
          </div>
        </div>
      </div>

      <FormActions>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="secondary" onClick={handleReview}>
          <Eye size={14} />
          Review in Workflow
        </Button>
        <Button variant="primary" onClick={handleStart}>
          <Play size={14} />
          Start Re-Run
        </Button>
      </FormActions>

    </Modal>
  )
}

export default ReRunModal
